import type { ReactNode } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useMutationState, type Mutation } from '@tanstack/react-query'
import { useTranslation } from 'next-i18next'
import { twJoin } from 'tailwind-merge'
import { LikeButton } from 'shared/components/like/like-button'
import useUser from 'shared/hooks/use-user'
import EllipseIcon from 'shared/icons/ellipse-icon'
import type { AttachmentsRefInterface } from 'modules/attachments/components/context'
import { PostAttachmentsProvider } from 'modules/attachments/components/context'
import AttachmentsPreview from 'modules/attachments/components/preview'
import { usePageComments } from 'modules/comments/api/get-page-comments'
import { commentsKeys } from 'modules/comments/api/key-factory'
import { useLikeComment } from 'modules/comments/api/like-comment'
import type { AddCommentRequestType, UpdateCommentRequestType } from 'modules/comments/api/types'
import { useUnlikeComment } from 'modules/comments/api/unlike-comment'
import { useUpdateComment } from 'modules/comments/api/update-comment'
import CommentActions from 'modules/comments/components/comment-actions'
import { CommentEditor } from 'modules/comments/components/comment-editor'
import { TextWithMentions } from 'modules/comments/components/comment-editor/deserialize-mention-to-html'
import type { CommentInterface } from 'modules/comments/components/comment/comment.types'
import { SubmittedComment } from 'modules/comments/components/comment/submitted-comment'
import { CommentHeader } from 'modules/comments/components/comment/ui/comment-header'
import { CommentLayout } from 'modules/comments/components/comment/ui/comment-layout'
import { useCommentsContext } from 'modules/comments/components/comments-list'
import { CommentListWithLoader } from 'modules/comments/components/comments-list/comment-list-with-loader'
import { MessageBox } from 'modules/comments/components/message-box/message-box'
import { useHighlighting } from 'modules/comments/hooks/use-highlighting'
import type { OnErrorType, CommentsTypeEnum } from 'modules/comments/types'
import { EditorMode } from 'modules/comments/types'
import { isCommunityExtra } from 'modules/comments/utils/comments-extra-type-guards'
import {
  UnderModerationPlaceholder,
  shouldHideUnderModerationContent,
} from 'modules/community/components/under-moderation-placeholder'
import type { PostAttachmentType } from 'modules/community/types/post-attachment'
import type { CommentsSearchInterface } from 'modules/community/types/post-interface'
import { escapeAttachmentLocalFields } from 'modules/community/utils/attachments'

const LINE_HEIGHT_OFFSET = 25

export interface CommentProps {
  data: CommentInterface | CommentsSearchInterface
  level: 0 | 1
  activeComment?: CommentInterface | null
  setActiveComment?: (comment: CommentInterface | null) => void
  onError?: OnErrorType
  previewOnly?: boolean
  highlightedCommentId?: number | null
  onHighlightComplete?: (commentId: number) => void
}

export const Comment = ({
  data,
  level,
  activeComment,
  setActiveComment,
  onError,
  previewOnly = false,
  highlightedCommentId,
  onHighlightComplete,
}: CommentProps): ReactNode => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { commentsType, resourceId, readOnly, extra, currentSortOption, targetCommentId } =
    useCommentsContext()
  const [isRepliesOpen, setIsRepliesOpen] = useState((data.children?.length ?? 0) > 0)
  const replyTargetCommentId = data.children?.some(child => child.id === targetCommentId)
    ? targetCommentId
    : undefined
  const replyQuery = usePageComments({
    commentsType,
    pageId: resourceId,
    parentId: data.id,
    sortOption: currentSortOption,
    initialData: data.children ?? undefined,
    targetCommentId: replyTargetCommentId,
    enabled: level === 0 && !previewOnly && isRepliesOpen,
    onError,
  })
  const replies = previewOnly ? (data.children ?? []) : (replyQuery.data ?? [])
  const replyAdditions = useMutationState<
    {
      mutationId: number
      variables: AddCommentRequestType<CommentsTypeEnum>
      submittedAt: number
      commentId: number | undefined
    }[],
    Mutation<CommentInterface, Error, AddCommentRequestType<CommentsTypeEnum>>
  >({
    filters: {
      exact: true,
      mutationKey: commentsKeys.add(commentsType, resourceId),
    },
    select: mutation => {
      const { variables, status, submittedAt, data: addedComment } = mutation.state
      if (
        variables?.parent !== `/api/comment/comments/${data.id}` ||
        (status !== 'pending' && status !== 'success')
      )
        return []
      return [
        { mutationId: mutation.mutationId, variables, submittedAt, commentId: addedComment?.id },
      ]
    },
  }).flat()
  const pendingReplies = replyAdditions.filter(
    ({ commentId }) =>
      commentId === undefined ||
      (!replyQuery.deletedCommentIds.includes(commentId) &&
        !replies.some(reply => reply.id === commentId) &&
        !replyQuery.resolvedFocusCommentIds.includes(commentId)),
  )
  const { totalReplies } = data
  const [isEditing, setIsEditing] = useState(false)
  const [attachments, setAttachments] = useState<PostAttachmentType[]>([])
  const pendingEdit = useMutationState<
    UpdateCommentRequestType,
    Mutation<CommentInterface, Error, UpdateCommentRequestType>
  >({
    filters: {
      exact: true,
      mutationKey: commentsKeys.update(commentsType, resourceId, data.id),
      status: 'pending',
    },
    select: mutation => mutation.state.variables!,
  }).at(-1)
  const displayedText =
    pendingEdit?.text ?? ('highlight' in data ? data.highlight.comment || data.text : data.text)
  const updateCommentMutation = useUpdateComment({
    commentId: data.id,
    commentsType,
    pageId: resourceId,
    onSuccess: () => {
      setIsEditing(false)
    },
  })
  const likeCommentMutation = useLikeComment({
    commentId: data.id,
    commentsType,
    pageId: resourceId,
    onError,
  })
  const unlikeCommentMutation = useUnlikeComment({
    commentId: data.id,
    commentsType,
    pageId: resourceId,
    onError,
  })
  const pendingLikeStates = useMutationState<boolean, Mutation<void, Error, boolean>>({
    filters: {
      exact: true,
      mutationKey: commentsKeys.toggleLike(commentsType, resourceId, data.id),
      status: 'pending',
    },
    select: mutation => mutation.state.variables!,
  })
  const isLiked = pendingLikeStates.at(-1) ?? data.isLiked
  const likesCount = data.likesCount + (isLiked === data.isLiked ? 0 : isLiked ? 1 : -1)
  const isDeleting = replyQuery.deletedCommentIds.includes(data.id)

  const attachmentsRef = useRef<AttachmentsRefInterface>(null)
  const lineRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const resizeObserverRef = useRef<ResizeObserver | null>(null)
  const {
    elementRef: commentRef,
    isHighlighted,
    ref: highlightRef,
  } = useHighlighting({
    commentId: data.id,
    highlightedCommentId,
    onHighlightComplete,
  })
  const isCommentOwner = data.canDeleteComment
  const commentTimestamp = new Date(data.updatedAt ?? data.createdAt).getTime()
  const wasEdited = data.updatedAt !== data.createdAt
  const isCommunityComments = isCommunityExtra(extra)
  const hideContent = shouldHideUnderModerationContent(data.underModeration, user)
  const interactionsDisabled = Boolean(readOnly) || hideContent

  const isSearchComment = 'highlight' in data

  const calculateLineHeight = useCallback(() => {
    if (containerRef.current && lineRef.current) {
      const container = containerRef.current
      const line = lineRef.current
      const containerHeight = container.scrollHeight

      const lastReplyElement = container.lastElementChild?.lastElementChild

      if (lastReplyElement instanceof HTMLElement) {
        const lastReplyHeight = lastReplyElement.offsetHeight
        const lineHeight = containerHeight - lastReplyHeight - LINE_HEIGHT_OFFSET
        line.style.height = `${lineHeight}px`
      }
    }
  }, [])
  const setContainerRef = useCallback(
    (element: HTMLDivElement | null): void => {
      resizeObserverRef.current?.disconnect()
      containerRef.current = element
      if (element === null) return
      const resizeObserver = new ResizeObserver(calculateLineHeight)
      resizeObserver.observe(element)
      resizeObserverRef.current = resizeObserver
      calculateLineHeight()
    },
    [calculateLineHeight],
  )
  const setLineRef = useCallback(
    (element: HTMLDivElement | null): void => {
      lineRef.current = element
      calculateLineHeight()
    },
    [calculateLineHeight],
  )

  const toggleReplies = (): void => {
    setIsRepliesOpen(previous => !previous)
  }

  const handleReply = (): void => {
    commentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    setActiveComment?.(null)
    // NOTE: It's a workaround to have the comment field re-created due to React life-cycle. Since Slate is uncontrollable and we can only use initValue on mount component
    setTimeout(() => setActiveComment?.(data), 10)
    if (level === 0) {
      setIsRepliesOpen(true)
    }
  }

  const handleLikeClick = (): void => {
    if (isLiked) unlikeCommentMutation.mutate(false)
    else likeCommentMutation.mutate(true)
  }

  const handleEditComment = async (
    value: string,
    attachments?: PostAttachmentType[],
  ): Promise<void> => {
    if (updateCommentMutation.isPending) return
    try {
      await updateCommentMutation.mutateAsync({
        text: value,
        attachments: escapeAttachmentLocalFields(attachments),
      })
    } catch (error) {
      onError?.(error)
    }
  }

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false)
  }, [])

  return (
    <PostAttachmentsProvider
      ref={attachmentsRef}
      attachments={hideContent ? [] : data.attachments}
      isEditable={isEditing && !hideContent}
      onChange={setAttachments}
    >
      {isEditing && !hideContent ? (
        <div hidden={pendingEdit !== undefined}>
          <CommentEditor
            initialHtml={data.text}
            initialMentions={data.mentions}
            handleAddComment={handleEditComment}
            isRoot={false}
            mode={EditorMode.Edit}
            setActiveComment={setActiveComment ?? (() => {})}
            onCancel={handleCancelEditing}
            editableComment={data}
            className="mt-5"
          />
        </div>
      ) : null}
      {(!isEditing || hideContent || pendingEdit !== undefined) && (
        <div
          className={twJoin('relative', isDeleting && 'hidden')}
          ref={setContainerRef}
          id={`comment-${data.id}`}
          data-testid="comment"
        >
          <CommentLayout
            userName={data.userName}
            userAvatarUrl={data.userAvatarUrl}
            level={level}
            threadLine={
              isRepliesOpen && (
                <div
                  ref={setLineRef}
                  className="absolute top-[40px] left-[20px] w-px bg-text-secondary/30"
                />
              )
            }
            scrollRef={highlightRef}
            className={
              data.id === activeComment?.id || isHighlighted ? 'bg-bg-accent-active/20' : undefined
            }
            header={
              <CommentHeader
                userName={data.userName}
                userId={data.userId}
                isCommunity={isCommunityComments}
                commentTimestamp={commentTimestamp}
                wasEdited={wasEdited}
                isPinned={data?.isPinned ?? false}
                highlightedAuthorHTML={isSearchComment ? data.highlight?.author : undefined}
              />
            }
            footer={
              <>
                <div className="mt-1.5 flex translate-y-1 items-center gap-2 text-sm">
                  {!hideContent && (
                    <LikeButton
                      handleLikeClick={handleLikeClick}
                      isLiked={isLiked}
                      likesCount={likesCount}
                      commentId={data.id}
                      readOnly={interactionsDisabled || pendingLikeStates.length > 0}
                    />
                  )}
                  {!interactionsDisabled && (
                    <button
                      data-testid="comment-reply"
                      type="button"
                      className="cursor-pointer font-medium text-text-secondary hover:text-text-link"
                      onClick={handleReply}
                    >
                      {t('comments.components.comments.reply_label')}
                    </button>
                  )}
                  {totalReplies > 0 && !hideContent && <EllipseIcon />}
                  {level === 0 && (totalReplies > 0 || pendingReplies.length > 0) && (
                    <div
                      data-testid="comment-reply-count"
                      className="mr-2 flex cursor-pointer text-text-link"
                      onClick={toggleReplies}
                    >
                      {isRepliesOpen
                        ? t('comments.components.comments.responses_label_hide')
                        : `${totalReplies} ${t(
                            totalReplies === 1
                              ? 'comments.components.comments.responses_label_one'
                              : 'comments.components.comments.responses_label_other',
                          )}`}
                    </div>
                  )}
                </div>
                {!isCommunityComments && !interactionsDisabled && activeComment?.id === data.id && (
                  <MessageBox
                    parentId={Array.isArray(data.parent) ? data.id : data.parent.id}
                    setActiveComment={setActiveComment}
                    level={1}
                    onError={onError}
                  />
                )}
              </>
            }
          >
            {hideContent ? (
              <UnderModerationPlaceholder type="comment" className="mt-1 md:mt-4" />
            ) : (
              <>
                <TextWithMentions
                  data-testid="comment-text"
                  className="mt-1 text-start text-text-primary/70 [word-wrap:break-word] md:mt-4 [&_a]:text-text-link [&_a]:no-underline [&_a:hover]:underline [&_p]:m-0 [&_p]:p-0"
                  text={displayedText}
                  mentions={data.mentions}
                />
                <AttachmentsPreview className={twJoin(!!attachments?.length && 'pt-4')} />
              </>
            )}
            {(isCommentOwner || isCommunityComments) && !hideContent && (
              <CommentActions
                commentData={data}
                className="absolute top-3 right-3.5 justify-self-end"
                onEdit={() => {
                  setIsEditing(true)
                }}
              />
            )}
          </CommentLayout>

          {!!isRepliesOpen && (
            <div className="mt-5 ml-[50px] flex flex-col space-y-5 md:mt-6 md:space-y-6">
              <CommentListWithLoader
                items={replies}
                renderItem={reply => (
                  <Comment
                    key={reply.id}
                    data={reply}
                    level={1}
                    setActiveComment={setActiveComment}
                    activeComment={activeComment}
                    onError={onError}
                    highlightedCommentId={highlightedCommentId}
                    onHighlightComplete={onHighlightComplete}
                  />
                )}
                onLoadMore={() => {
                  if (replyQuery.isError) void replyQuery.refetch()
                  else void replyQuery.fetchNextPage()
                }}
                isLoadingMore={replyQuery.isFetching}
                loadMoreLabel={t('comments.components.comments.load_more_label')}
              />
              {!previewOnly &&
                pendingReplies.map(({ mutationId, variables, submittedAt }) => (
                  <SubmittedComment
                    key={mutationId}
                    variables={variables}
                    submittedAt={submittedAt}
                    level={1}
                  />
                ))}
            </div>
          )}
        </div>
      )}
    </PostAttachmentsProvider>
  )
}
